import ExcelJS from "exceljs";
import {
  applyDataBorder,
  getCompanyName,
  hideSheetGridlines,
  moneyFmt,
  styleLightHeader,
} from "./payrollReportExcelShared";
import { formatInrFull, statusBadgeLabel } from "./payrollDashboardAnalytics";
import {
  aspectsToSections,
  cellValue,
  resolveCompareSummaryRows,
} from "./payrollDashboardCompareExport";

function downloadWorkbook(workbook, filename) {
  return workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return filename;
  });
}

function addTitleBlock(ws, title, subtitle) {
  ws.mergeCells(1, 1, 1, 8);
  const t = ws.getCell(1, 1);
  t.value = title;
  t.font = { bold: true, size: 14, color: { argb: "FF111827" } };
  ws.mergeCells(2, 1, 2, 8);
  const s = ws.getCell(2, 1);
  s.value = subtitle;
  s.font = { size: 10, color: { argb: "FF6B7280" } };
  ws.getRow(1).height = 22;
  ws.getRow(2).height = 16;
}

function writeSectionTable(ws, startRow, section, rows) {
  let r = startRow;
  ws.getCell(r, 1).value = section.title;
  ws.getCell(r, 1).font = { bold: true, size: 11 };
  r += 1;
  section.columns.forEach((col, i) => {
    const cell = ws.getCell(r, i + 1);
    cell.value = col.label;
    styleLightHeader(cell, "FFFEF3C7");
  });
  r += 1;
  rows.forEach((row) => {
    section.columns.forEach((col, i) => {
      const cell = ws.getCell(r, i + 1);
      const v = cellValue(row, col);
      cell.value = v;
      if (col.money && typeof v === "number") moneyFmt(cell);
      applyDataBorder(cell);
    });
    r += 1;
  });
  section.columns.forEach((col, i) => {
    ws.getColumn(i + 1).width = Math.max(12, String(col.label).length + 2);
  });
  return r + 1;
}

/**
 * Export Payroll Dashboard analytics workbook (pulse + statutory + masters + compare).
 */
export async function exportPayrollDashboardExcel({
  analytics = null,
  readiness = null,
  regimeFallback = null,
  periodLabel = "",
  compareAspects = [],
} = {}) {
  const company = getCompanyName();
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "PaceHRM";
  const label = periodLabel || analytics?.periodLabel || "";
  const totals = analytics?.totals || {};

  const ws1 = workbook.addWorksheet("Company pulse");
  hideSheetGridlines(ws1);
  addTitleBlock(
    ws1,
    `${company} - Payroll Analytics`,
    `${label} | ${statusBadgeLabel(analytics?.status)} | ${new Date().toLocaleString("en-IN")}`
  );
  const pulseRows = [
    ["Headcount", analytics?.headcount ?? ""],
    ["Payroll cost", totals.totalCost || 0],
    ["Cash outflow", totals.totalCashFlow || 0],
    ["Bank / Net", totals.netPay || 0],
    ["Fixed cost", totals.fixedCost || readiness?.structureFixedCost || 0],
    ["Avg fixed / emp", totals.avgFixedCost || readiness?.avgStructureFixed || 0],
    ["Avg earned / emp", totals.avgEarnedCost || 0],
    ["Gross pay", totals.grossPay || 0],
    ["Statutory liabilities", totals.statutoryLiabilities || 0],
    ["TDS", totals.tds || 0],
    ["PF employer", totals.pfEmployer || 0],
    ["ESI employer", totals.esiEmployer || 0],
    ["Professional tax", totals.professionalTax || 0],
    ["Old regime", analytics?.taxRegime?.oldCount ?? regimeFallback?.oldCount ?? 0],
    ["New regime", analytics?.taxRegime?.newCount ?? regimeFallback?.newCount ?? 0],
  ];
  ["Metric", "Value"].forEach((h, i) => {
    const cell = ws1.getCell(4, i + 1);
    cell.value = h;
    styleLightHeader(cell, "FFD1FAE5");
  });
  pulseRows.forEach((row, idx) => {
    const r = 5 + idx;
    ws1.getCell(r, 1).value = row[0];
    ws1.getCell(r, 2).value = row[1];
    if (idx >= 1 && idx <= 12 && typeof row[1] === "number") moneyFmt(ws1.getCell(r, 2));
    applyDataBorder(ws1.getCell(r, 1));
    applyDataBorder(ws1.getCell(r, 2));
  });
  ws1.getColumn(1).width = 28;
  ws1.getColumn(2).width = 18;

  const ws3 = workbook.addWorksheet("Masters");
  hideSheetGridlines(ws3);
  addTitleBlock(ws3, `Master-wise (${analytics?.groupBy || "department"})`, label);
  const masterHeaders = ["Master", "HC", "Fixed", "Earned", "Cash out", "Net", "TDS"];
  masterHeaders.forEach((h, i) => {
    const cell = ws3.getCell(4, i + 1);
    cell.value = h;
    styleLightHeader(cell, "FFE0E7FF");
  });
  (analytics?.masters || []).forEach((row, idx) => {
    const r = 5 + idx;
    const vals = [
      row.label,
      row.headcount,
      row.fixedCost,
      row.earnedCost,
      row.cashOutflow,
      row.netPay,
      row.tds,
    ];
    vals.forEach((v, i) => {
      const cell = ws3.getCell(r, i + 1);
      cell.value = v;
      if (i >= 2) moneyFmt(cell);
      applyDataBorder(cell);
    });
  });
  masterHeaders.forEach((_, i) => {
    ws3.getColumn(i + 1).width = i === 0 ? 28 : 14;
  });

  const ws4 = workbook.addWorksheet("Comparison summary");
  hideSheetGridlines(ws4);
  addTitleBlock(
    ws4,
    analytics?.compareActive
      ? "Comparison summary (selected months)"
      : "Period comparison / series",
    label
  );
  const cmpRows = resolveCompareSummaryRows(analytics);
  const sections = aspectsToSections(compareAspects);
  let rowCursor = 4;
  sections.forEach((section) => {
    rowCursor = writeSectionTable(ws4, rowCursor, section, cmpRows);
  });

  if ((analytics?.compare || []).some((c) => (c.masters || []).length)) {
    const wsM2 = workbook.addWorksheet("Compare masters");
    hideSheetGridlines(wsM2);
    addTitleBlock(wsM2, "Master-wise by compare month", label);
    ["Month", "Master", "HC", "Fixed", "Earned", "Cash", "TDS"].forEach((h, i) => {
      const cell = wsM2.getCell(4, i + 1);
      cell.value = h;
      styleLightHeader(cell, "FFE0E7FF");
    });
    let r = 5;
    (analytics.compare || []).forEach((c) => {
      (c.masters || []).forEach((m) => {
        const vals = [
          c.payMonth,
          m.label,
          m.headcount,
          m.fixedCost,
          m.earnedCost,
          m.cashOutflow,
          m.tds,
        ];
        vals.forEach((v, i) => {
          const cell = wsM2.getCell(r, i + 1);
          cell.value = v;
          if (i >= 3) moneyFmt(cell);
          applyDataBorder(cell);
        });
        r += 1;
      });
    });
  }

  const wsA = workbook.addWorksheet("Alerts");
  hideSheetGridlines(wsA);
  addTitleBlock(wsA, "Payroll notifications", label);
  ["Severity", "Title", "Detail"].forEach((h, i) => {
    const cell = wsA.getCell(4, i + 1);
    cell.value = h;
    styleLightHeader(cell, "FFFECACA");
  });
  (analytics?.alerts || []).forEach((a, idx) => {
    const r = 5 + idx;
    wsA.getCell(r, 1).value = a.tone || "";
    wsA.getCell(r, 2).value = a.title || "";
    wsA.getCell(r, 3).value = a.detail || "";
    [1, 2, 3].forEach((c) => applyDataBorder(wsA.getCell(r, c)));
  });
  wsA.getColumn(1).width = 12;
  wsA.getColumn(2).width = 28;
  wsA.getColumn(3).width = 56;

  const wsM = workbook.addWorksheet("Movements");
  hideSheetGridlines(wsM);
  addTitleBlock(wsM, "Salary revisions & statutory changes", label);
  const rev = analytics?.revisions || {};
  const stCh = analytics?.statutoryChanges || {};
  [
    ["Revisions count", rev.count || 0],
    ["CTC delta", rev.ctcDelta || 0],
    ["With booking/arrears", rev.withArrearsBooking || 0],
    ["Statutory change rows", stCh.count || 0],
  ].forEach((row, i) => {
    const r = 4 + i;
    wsM.getCell(r, 1).value = row[0];
    wsM.getCell(r, 2).value = row[1];
    if (row[0] === "CTC delta") moneyFmt(wsM.getCell(r, 2));
    applyDataBorder(wsM.getCell(r, 1));
    applyDataBorder(wsM.getCell(r, 2));
  });

  const wsF = workbook.addWorksheet("FnF");
  hideSheetGridlines(wsF);
  addTitleBlock(wsF, "F&F / notice / leave encash / gratuity", label);
  const fnf = analytics?.fnf || {};
  [
    ["F&F count", fnf.count || 0],
    ["Drafts", fnf.draftCount || 0],
    ["Net total", fnf.netTotal || 0],
    ["Leave encashment", fnf.leaveEncashment || 0],
    ["Gratuity", fnf.gratuity || 0],
    ["Notice pay", fnf.noticePay || 0],
    ["Notice recovery", fnf.noticeRecovery || 0],
  ].forEach((row, i) => {
    const r = 4 + i;
    wsF.getCell(r, 1).value = row[0];
    wsF.getCell(r, 2).value = row[1];
    if (i >= 2) moneyFmt(wsF.getCell(r, 2));
    applyDataBorder(wsF.getCell(r, 1));
    applyDataBorder(wsF.getCell(r, 2));
  });

  const wsAtt = workbook.addWorksheet("Attendance vs Payroll");
  hideSheetGridlines(wsAtt);
  addTitleBlock(wsAtt, "Attendance process vs payroll (read-only)", label);
  ["Month", "Att scope", "Att processed", "Att open", "Att locked", "Payroll HC", "Payroll status"].forEach(
    (h, i) => {
      const cell = wsAtt.getCell(4, i + 1);
      cell.value = h;
      styleLightHeader(cell, "FFCCFBF1");
    }
  );
  (analytics?.attendanceCompare || []).forEach((row, idx) => {
    const r = 5 + idx;
    const vals = [
      row.payMonth,
      row.attendanceScope,
      row.attendanceProcessed,
      row.attendanceUnprocessed,
      row.attendanceMonthLocked ? "Yes" : "No",
      row.payrollHeadcount,
      row.payrollStatus,
    ];
    vals.forEach((v, i) => {
      const cell = wsAtt.getCell(r, i + 1);
      cell.value = v;
      applyDataBorder(cell);
    });
  });

  const wsL = workbook.addWorksheet("Liability snapshot");
  hideSheetGridlines(wsL);
  addTitleBlock(wsL, "Liability-related (provision + F&F only)", label);
  const liab = analytics?.liability || {};
  Object.entries({
    "Gratuity provision (register)": liab.gratuityProvisionMonthly,
    "F&F leave encashment": liab.fnfLeaveEncashment,
    "F&F gratuity": liab.fnfGratuity,
    "Notice pay": liab.fnfNoticePay,
    "Notice recovery": liab.fnfNoticeRecovery,
    "Revision arrears": liab.revisionArrears,
    "Loan deductions (register)": liab.loanDeductionsInRegister,
  }).forEach(([k, v], i) => {
    const r = 4 + i;
    wsL.getCell(r, 1).value = k;
    wsL.getCell(r, 2).value = Number(v) || 0;
    moneyFmt(wsL.getCell(r, 2));
    applyDataBorder(wsL.getCell(r, 1));
    applyDataBorder(wsL.getCell(r, 2));
  });

  const safeLabel = String(label || "Dashboard")
    .replace(/[^a-zA-Z0-9_]+/g, "_")
    .slice(0, 40);
  const filename = `Payroll_Dashboard_${safeLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  await downloadWorkbook(workbook, filename);
  return filename;
}

export { formatInrFull };
